// @flow

import { MD5 } from 'crypto-js';

import { HitchpinApi } from '../../api/Hitchpin';
import storefront_banner from '../../img/storefronts/background_banner_1.png';
import type { storefrontType, categorySummaryType } from '../../types';
import type {
  cardDetailsType,
  CategoryTableType,
} from '../../types/card/card_details_type';
import { CardInstanceType } from '../../components/Shared/HitchpinCard/CardInstanceType';
import { HitchpinCardTypeNames } from '../../components/Shared/HitchpinCard/HitchpinCardTypeNames';
import image_mask from '../../img/storefronts/no_image.png';
const getSortedImage = cardDetails => {
  const sortedImages = cardDetails?.images?.sort((a, b) => a.order - b.order);
  return sortedImages?.[0]?.image;
};

const getImageSource = (cardDetails: cardDetailsType, category: any) => {
  const isSupply = cardDetails.card_type === HitchpinCardTypeNames.Supply;
  const isDemand = cardDetails.card_type === HitchpinCardTypeNames.Demand;
  const isStorefront =
    cardDetails.card_type === HitchpinCardTypeNames.Storefront;

  if (isSupply)
    return getSortedImage(cardDetails) || cardDetails.image || image_mask;

  if (isDemand && category) {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const icon = category.icon || '';
    const type = category.jsonldtype;
    return `${baseUrl}icons/${icon}/${type}_svg/`;
  }
  if (isStorefront) return cardDetails?.sf_cover || storefront_banner;

  return image_mask;
};
const getCategoryTable = (
  categories: categorySummaryType[],
): CategoryTableType => {
  const flatten = (categories, result = {}) => {
    for (const category of categories) {
      result[category.id] = category;
      if (category.children && category.children.length) {
        flatten(category.children, result);
      }
    }
    return result;
  };

  return flatten(categories);
};

const getPricing = (cardDetails: cardDetailsType) => {
  return (cardDetails?.primary_pricing_detail || [])
    .map(item => item.detail)
    .join(' | ');
};

const getTitle = (
  cardDetails: cardDetailsType,
  category: categorySummaryType,
) => {
  if (cardDetails.card_type === HitchpinCardTypeNames.Supply)
    return cardDetails.title;
  return `Wanted: ${category.name}`;
};

function getPlural(word: string, count: number) {
  return count !== 1 ? `${word}s` : word;
}

const onCardClick = (cardDetails: cardDetailsType) => {
  if (cardDetails.is_promoted && !/Prerender/i.test(navigator.userAgent)) {
    const hash = MD5(cardDetails.updated).toString();
    HitchpinApi.promoted.checkPromoted(cardDetails.uid, hash);
  }
};

const getCardUrl = (cardDetails: cardDetailsType): string => {
  let cardUrl = `${cardDetails.frontend_url_seo}`;

  if (cardDetails.card_type === HitchpinCardTypeNames.Storefront)
    cardUrl = `/${cardDetails?.slug ?? ''}`;

  return cardUrl;
};

function toCardDetails(data: any): cardDetailsType {
  const instanceType = data.instance_type ?? '';
  switch (instanceType) {
    case CardInstanceType.Storefront:
      return convertFromStorefront(data);
    case CardInstanceType.StorefrontListing:
      return convertFromStorefrontListing(data);
    case CardInstanceType.MyOfferingListing:
      return convertFromMyListing(data, HitchpinCardTypeNames.Supply);
    case CardInstanceType.MyWantedListing:
      return convertFromMyListing(data, HitchpinCardTypeNames.Demand);
    case CardInstanceType.OfferingMatch:
      return convertFromOfferingMatch(data);
    case CardInstanceType.WantedMatch:
      return convertFromWantedMatch(data);
    case CardInstanceType.SimilarListing:
      return convertFromSimilarListing(data, HitchpinCardTypeNames.Supply);
    case CardInstanceType.SimilarWanted:
      return convertFromSimilarWanted(data, HitchpinCardTypeNames.Demand);
    default:
      return data;
  }
}

function convertFromOfferingMatch(offeringMatch: any): any {
  return {
    ...offeringMatch,
    card_type: HitchpinCardTypeNames.Demand,
    wanted_id: offeringMatch.id,
  };
}

function convertFromWantedMatch(wantedMatch: any): any {
  return {
    ...wantedMatch,
    card_type: HitchpinCardTypeNames.Supply,
    offering_id: wantedMatch.id,
    nationwide: wantedMatch.nationwide,
  };
}

function convertFromStorefrontListing(myListing: any): any {
  return {
    ...myListing,
    card_type: HitchpinCardTypeNames.Supply,
    offering_id: myListing.id,
  };
}

function convertFromSimilarListing(
  similarListing: any,
  cardType: HitchpinCardTypeNames,
): any {
  return {
    ...similarListing,
    card_type: cardType,
    offering_id: similarListing.id,
  };
}

function convertFromSimilarWanted(
  similarListing: any,
  cardType: HitchpinCardTypeNames,
): any {
  return {
    ...similarListing,
    card_type: cardType,
    wanted_id: similarListing.id,
  };
}

function convertFromMyListing(
  myListing: any,
  cardType: HitchpinCardTypeNames,
): any {
  let primary_pricing_detail = [];
  const delivery_provided = containsDeliveryKey(
    myListing.rendered_pricing_details,
  );
  if (cardType === HitchpinCardTypeNames.Supply)
    primary_pricing_detail = convertToPrimaryPricingDetail(
      myListing.rendered_pricing_details,
    );

  let isPromoted = false;
  const { start, end } = myListing.promoted_dates || {};
  if (!start || !end) {
    isPromoted = false;
  } else {
    const [year, month, day] = end.split('-').map(num => parseInt(num, 10));
    const endDate = new Date(year, month - 1, day);
    const today = new Date();

    isPromoted = today <= endDate;
  }

  return {
    ...myListing,
    primary_pricing_detail,
    card_type: cardType,
    delivery_provided,
    offering_id: myListing.id,
    is_promoted: isPromoted,
  };
}

function containsDeliveryKey(rendered_pricing_details) {
  for (const key in rendered_pricing_details) {
    if (key.includes('delivery')) {
      return true;
    }
  }
  return false;
}

function convertToPrimaryPricingDetail(renderedPricingDetails) {
  const firstPropertyKey = Object.keys(renderedPricingDetails)[0];
  const pricingDetails = renderedPricingDetails[firstPropertyKey] ?? [];

  return pricingDetails.map(detail => ({
    label: detail.label,
    detail: detail.detail,
  }));
}

function extractQuantity(detail: any) {
  // Match numerical value at the start of the string
  const match = detail.match(/^([\d,]+(\.\d+)?)/);
  if (match) return parseFloat(match[1].replace(/,/g, ''));

  return null;
}

function convertFromStorefront(storefront: storefrontType): cardDetailsType {
  return {
    uid: storefront.uid,
    id: storefront.id,
    user: storefront.owner ? 1 : 0,
    title: storefront.name,
    subtitle: '',
    description: storefront.description,
    location: null,
    approximate_location: {
      type: 'Point',
      properties: { radius: 0 },
      geometry: {
        type: 'Point',
        coordinates: [0, 0],
      },
    },
    address: `${storefront.city}, ${storefront.state}`,
    city: storefront.city,
    state: storefront.state,
    state_abbr: storefront.state_abbr,
    wish: false,
    followed: storefront.followed ? 1 : 0,
    owner: storefront.owner,
    images: [
      {
        id: 0,
        offering: storefront.id,
        image: storefront.image,
        local_identifier: '',
        thumbnail: storefront.cover_image_small,
        order: 0,
      },
    ],
    videos: [],
    contract_pending: false,
    sold: false,
    frontend_url: '',
    frontend_url_seo: '',
    published: storefront.published,
    featured: false,
    timestamp: storefront.updated,
    updated: storefront.updated,
    has_videos: false,
    delivery_provided: false,
    expires_on: null,
    expired: false,
    card_type: HitchpinCardTypeNames.Storefront,
    listings: storefront.listings,
    followers: storefront.followers,
    slug: storefront.slug,
    sf_image: storefront.image,
    sf_cover: storefront.cover_image,
    quantity_available: null,
    is_promoted: 0,
    instance_type: CardInstanceType.CardDetails,
    sf_category_names: storefront.sf_category_names,
    nationwide: storefront.nationwide,
  };
}

const addInstanceType = (response: any, instance_type: any) => {
  if (response?.body) {
    if (Array.isArray(response.body)) {
      const enrichedStorefronts = response.body.map(storefront => ({
        ...storefront,
        instance_type,
      }));
      return { ...response, body: enrichedStorefronts };
    } else if (Array.isArray(response.body.results)) {
      const enrichedResults = response.body.results.map(storefront => ({
        ...storefront,
        instance_type,
      }));
      return {
        ...response,
        body: {
          ...response.body,
          results: enrichedResults,
        },
      };
    } else {
      const enrichedResult = {
        ...response,
        body: {
          ...response.body,
          instance_type,
        },
      };
      return enrichedResult;
    }
  } else if (Array.isArray(response)) {
    const enrichedResults = response.map(item => ({
      ...item,
      instance_type,
    }));
    return enrichedResults;
  }
  return response;
};

export {
  getImageSource,
  getCategoryTable,
  getCardUrl,
  getPricing,
  getTitle,
  getPlural,
  onCardClick,
  toCardDetails,
  addInstanceType,
  extractQuantity,
};
